/**
 * InvestigationViewPage — read-only view of a saved investigation snapshot.
 * Reuses the existing AnalysisResults / FixerResults / FitResults components.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AppHeader from './layout/AppHeader';
import AnalysisResults from './AnalysisResults';
import FixerResults from './FixerResults';
import FitResults from './FitResults';
import TagChips from './TagChips';
import VisibilityToggle from './VisibilityToggle';
import { useAuth } from '../context/AuthContext';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvestigationViewPage() {
  const { investigationId } = useParams();
  const { user: me } = useAuth();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [section, setSection] = useState('analysis');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfName, setPdfName] = useState('QUOD_original_record.pdf');
  const [pdfKind, setPdfKind] = useState(null);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const exportStoredPdf = async (kind, id) => {
    if (!id) { setErr(kind === 'fit' ? 'This snapshot has no retained analysis ID for a latest saved Fit export.' : 'This snapshot has no retained original record ID to export.'); return; }
    setErr(null);
    try {
      const response = await fetch(`${API_URL}/api/export/${kind}/${encodeURIComponent(id)}`, { credentials: 'include' });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(formatBackendDetail(body.detail) || `Stored PDF export failed (${response.status})`);
      }
      const blob = await response.blob();
      if (blob.type !== 'application/pdf') throw new Error('The export did not return a PDF.');
      const safeName = String(inv?.company_name || 'record').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 80);
      const safeId = String(id).replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 64);
      setPdfName(`QUOD_${kind}${kind === 'fit' ? '_latest_for_analysis' : ''}_${safeName}_${safeId}.pdf`);
      setPdfKind(kind);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (error) { setErr(error.message); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/investigations/${investigationId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setInv(await res.json());
      } catch (e) { setErr(e.message); }
    })();
  }, [investigationId]);

  const alertReadOnly = () => alert('This is a read-only snapshot. Open the company from the Pipeline to run new actions.');

  const saveTags = async (newTags) => {
    const res = await fetch(`${API_URL}/api/investigations/${investigationId}/tags`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tags: newTags }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(formatBackendDetail(d.detail) || `HTTP ${res.status}`);
    }
    const { tags } = await res.json();
    setInv({ ...inv, tags });
  };

  const saveVisibility = async (next) => {
    const res = await fetch(`${API_URL}/api/investigations/${investigationId}/visibility`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visibility: next }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(formatBackendDetail(d.detail) || `HTTP ${res.status}`);
    }
    const { visibility } = await res.json();
    setInv({ ...inv, visibility });
  };

  return (
    <>
      <AppHeader active="pipeline" />
      <div className="page-shell" data-testid="investigation-view-page">
        {err && <div className="auth-error">{err}</div>}
        {!inv && !err && <div className="panel"><div className="panel-body">Loading investigation…</div></div>}
        {inv && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{inv.company_name}</h2>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
                  Saved {new Date(inv.created_at).toLocaleString()}
                  {inv.created_by_email ? ` · ${inv.created_by_email}` : ''}
                  {inv.scope_label ? ` · ${inv.scope_label}` : ''}
                  {inv.analysis_lens ? ` · lens: ${inv.analysis_lens}` : ''}
                </p>
                {inv.headline_summary && (
                  <p style={{ fontSize: 13, fontStyle: 'italic', marginTop: 6 }}>{inv.headline_summary}</p>
                )}
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }} data-testid="inv-visibility-row">
                  <VisibilityToggle
                    visibility={inv.visibility}
                    canEdit={me?.id && inv.created_by === me.id}
                    onChange={saveVisibility}
                    testid="inv-visibility-toggle"
                  />
                </div>
                <div style={{ marginTop: 8 }} data-testid="inv-tags">
                  <TagChips
                    tags={inv.tags || []}
                    editable
                    onChange={saveTags}
                    testidPrefix="inv-tag"
                  />
                </div>
              </div>
              <span className="badge badge-type" style={{ fontSize: 10 }}>READ-ONLY SNAPSHOT</span>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <button className={`nav-btn ${section === 'analysis' ? 'nav-btn-active' : ''}`} onClick={() => setSection('analysis')} data-testid="inv-tab-analysis">Analysis</button>
              {inv.fixer && <button className={`nav-btn ${section === 'fixer' ? 'nav-btn-active' : ''}`} onClick={() => setSection('fixer')} data-testid="inv-tab-fixer">Fix</button>}
              {inv.fit && <button className={`nav-btn ${section === 'fit' ? 'nav-btn-active' : ''}`} onClick={() => setSection('fit')} data-testid="inv-tab-fit">Fit</button>}
            </div>
            <p className="briefing-muted" data-testid="inv-export-scope">Analysis and Fix PDFs open the original stored records named by this snapshot. Fit PDFs export the latest saved Fit for this analysis, which may differ from the frozen Fit displayed here. No export regenerates research or creates a new assessment.</p>
            {section === 'fit' && <p className="briefing-muted" data-testid="inv-fit-export-scope"><strong>Fit export scope:</strong> latest saved Fit for analysis {inv.analysis?.id || 'not retained'}, not necessarily the frozen Fit in this snapshot.</p>}
            {pdfUrl && <section className="panel" data-testid="inv-pdf-export" aria-label={pdfKind === 'fit' ? 'Latest saved Fit for original analysis PDF' : 'Original stored record PDF'}>
              <div className="panel-body"><a href={pdfUrl} download={pdfName} className="btn btn-primary btn-sm" data-testid="inv-pdf-download">Download PDF</a> <button type="button" className="btn btn-outline btn-sm" onClick={() => setPdfUrl(null)}>Close PDF</button><p>{pdfKind === 'fit' ? 'This PDF is the latest saved Fit for the original analysis; it may differ from the frozen snapshot. Download or use the viewer controls to save or print it.' : 'Download or use the PDF viewer controls to save or print the original record.'}</p><iframe title={pdfKind === 'fit' ? 'Latest saved Fit for original analysis PDF' : 'Original stored record PDF'} src={pdfUrl} style={{ width: '100%', minHeight: 600, border: 0 }} /></div>
            </section>}

            {section === 'analysis' && inv.analysis && (
              <AnalysisResults
                analysis={inv.analysis}
                onReset={alertReadOnly}
                onGenerateRoadmap={alertReadOnly}
                onDownloadPDF={() => exportStoredPdf('analysis', inv.analysis?.id)}
                onDownloadPNG={alertReadOnly}
                loading={false}
              />
            )}
            {section === 'fixer' && inv.fixer && (
              <FixerResults
                roadmap={inv.fixer}
                onBack={() => setSection('analysis')}
                onDownloadPDF={() => exportStoredPdf('roadmap', inv.fixer?.id)}
                onDownloadPNG={alertReadOnly}
                onGenerateFit={alertReadOnly}
                fitLoading={false}
              />
            )}
            {section === 'fit' && inv.fit && (
              <FitResults
                fit={inv.fit}
                analysisId={inv.analysis?.id}
                onBack={() => setSection('fixer')}
                onDownloadPDF={() => exportStoredPdf('fit', inv.analysis?.id)}
                onDownloadPNG={alertReadOnly}
              />
            )}

            {inv.approved_signals && inv.approved_signals.length > 0 && section === 'analysis' && (
              <div className="panel" style={{ marginTop: 16 }} data-testid="inv-approved-signals">
                <div className="panel-header">Approved signals at time of save ({inv.approved_signals.length})</div>
                <div className="panel-body" style={{ padding: 0 }}>
                  <ul style={{ margin: 0, padding: '8px 16px', fontSize: 12, listStyle: 'disc' }}>
                    {inv.approved_signals.slice(0, 20).map((s, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        <strong>{s.title || s.signal_type || 'signal'}:</strong>{' '}
                        <span style={{ color: 'var(--text-secondary)' }}>{(s.snippet || s.description || '').slice(0, 200)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
